import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, HStack, Button } from '@chakra-ui/react';
import '../styles/commentContent.css';

interface CommentRichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string | number;
  disabled?: boolean;
  /** On mobile: contentEditable only after user taps the field */
  requireTapToEdit?: boolean;
}

const exec = (command: string, value?: string) => {
  document.execCommand(command, false, value);
};

/* Inline SVG icons (no extra dependency) */
const IconBold = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /></svg>
);
const IconItalic = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></svg>
);
const IconUnderline = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3v7a6 6 0 0 0 12 0V3" /><line x1="4" y1="21" x2="20" y2="21" /></svg>
);
const IconListBullet = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
);
const IconListOrdered = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><path d="M4 6h1v4" /><path d="M4 10h2" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></svg>
);
const IconAlignLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="15" y1="12" x2="3" y2="12" /><line x1="17" y1="18" x2="3" y2="18" /></svg>
);
const IconAlignCenter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="17" y1="12" x2="7" y2="12" /><line x1="19" y1="18" x2="5" y2="18" /></svg>
);
const IconAlignRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="12" x2="9" y2="12" /><line x1="21" y1="18" x2="7" y2="18" /></svg>
);

export const CommentRichEditor: React.FC<CommentRichEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write a comment...',
  minHeight = '80px',
  disabled = false,
  requireTapToEdit = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(!requireTapToEdit);

  useEffect(() => {
    setEditing(!requireTapToEdit);
  }, [requireTapToEdit]);

  // Sync from prop only when clearing (e.g. after submit), so we don't fight with user typing
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!value || value.trim() === '') {
      const empty = el.innerHTML.replace(/<p><br\s*\/?><\/p>|^<br\s*\/?>$/gi, '').trim();
      if (empty !== '') el.innerHTML = '';
    }
  }, [value]);

  const emitChange = useCallback(() => {
    const html = ref.current?.innerHTML ?? '';
    onChange(html);
  }, [onChange]);

  const onInput = useCallback(() => {
    emitChange();
  }, [emitChange]);

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const wrapSelectionInTag = useCallback((tagName: string) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      const el = document.createElement(tagName);
      el.appendChild(document.createTextNode('\u00A0'));
      range.insertNode(el);
      range.setStart(el, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      const el = document.createElement(tagName);
      try {
        range.surroundContents(el);
      } catch {
        const fragment = range.extractContents();
        el.appendChild(fragment);
        range.insertNode(el);
      }
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          exec('bold');
          return;
        case 'i':
          e.preventDefault();
          wrapSelectionInTag('em');
          emitChange();
          return;
        case 'u':
          e.preventDefault();
          exec('underline');
          return;
        case '7':
          if (e.shiftKey) {
            e.preventDefault();
            exec('insertOrderedList');
            return;
          }
          break;
        case '8':
          if (e.shiftKey) {
            e.preventDefault();
            exec('insertUnorderedList');
            return;
          }
          break;
        default:
          break;
      }
    }
  }, [wrapSelectionInTag, emitChange]);

  const trigger = (command: string, value?: string) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (command === 'italic') {
      wrapSelectionInTag('em');
    } else if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (range.collapsed) {
          const block = document.createElement(command === 'insertUnorderedList' ? 'ul' : 'ol');
          const li = document.createElement('li');
          li.appendChild(document.createTextNode('\u00A0'));
          block.appendChild(li);
          range.insertNode(block);
          range.setStart(li, 0);
          range.setEnd(li, 0);
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          exec(command, value);
        }
      } else {
        exec(command, value);
      }
    } else {
      exec(command, value);
    }
    emitChange();
  };

  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  const activateEditor = () => {
    if (!editing && requireTapToEdit && !disabled) {
      setEditing(true);
      requestAnimationFrame(() => ref.current?.focus());
    }
  };

  return (
    <Box width="100%">
      {editing && (
      <HStack gap={1} mb={2} flexWrap="wrap">
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('bold')} title="Podebljano (Ctrl+B)" aria-label="Podebljano">
          <IconBold />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('italic')} title="Kurziv (Ctrl+I)" aria-label="Kurziv">
          <IconItalic />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('underline')} title="Podvučeno (Ctrl+U)" aria-label="Podvučeno">
          <IconUnderline />
        </Button>
        <Box w="1px" h="5" bg="gray.300" mx={1} />
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('insertUnorderedList')} title="Lista sa tačkama (Ctrl+Shift+8)" aria-label="Lista">
          <IconListBullet />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('insertOrderedList')} title="Numerisana lista (Ctrl+Shift+7)" aria-label="Numerisana lista">
          <IconListOrdered />
        </Button>
        <Box w="1px" h="5" bg="gray.300" mx={1} />
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('justifyLeft')} title="Poravnaj levo" aria-label="Levo">
          <IconAlignLeft />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('justifyCenter')} title="Centrirano" aria-label="Centar">
          <IconAlignCenter />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('justifyRight')} title="Poravnaj desno" aria-label="Desno">
          <IconAlignRight />
        </Button>
      </HStack>
      )}
      <div
        ref={ref}
        className="comment-html-content comment-rich-editor-content"
        contentEditable={!disabled && editing}
        suppressContentEditableWarning
        onInput={onInput}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onClick={activateEditor}
        data-placeholder={placeholder}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
          padding: '8px 12px',
          border: '1px solid var(--chakra-colors-gray-200)',
          borderRadius: '6px',
          backgroundColor: editing ? 'white' : 'var(--chakra-colors-gray-50)',
          fontSize: '14px',
          outline: 'none',
          cursor: requireTapToEdit && !editing ? 'text' : 'text',
        }}
      />
    </Box>
  );
};

export default CommentRichEditor;
